import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Clock3, KeyRound, ShieldAlert, X } from 'lucide-react';
import { api } from '../lib/api';

const statusStyles = {
  pending: 'border-amber-300 bg-amber-50 text-amber-800',
  approved: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  denied: 'border-red-300 bg-red-50 text-red-700',
  expired: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  cancelled: 'border-zinc-200 bg-zinc-50 text-zinc-600',
};

const policyStyles = {
  internal_authentication_ready: 'text-emerald-700',
  internal_evidence_ready: 'text-zinc-700',
  internal_foundation: 'text-zinc-700',
  not_ready: 'text-amber-800',
  blocked: 'text-red-700',
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function requestStatusLabel(status) {
  return String(status || 'pending').replaceAll('_', ' ');
}

function policyStatusLabel(status) {
  return String(status || 'not_ready').replaceAll('_', ' ');
}

function RequestStatus({ status }) {
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${statusStyles[status] || statusStyles.pending}`}>{requestStatusLabel(status)}</span>;
}

function timeRemaining(expiresAt) {
  const milliseconds = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return 'Expired';
  const minutes = Math.ceil(milliseconds / 60000);
  return minutes === 1 ? 'Expires in 1 min' : `Expires in ${minutes} min`;
}

function SummaryMetric({ label, value }) {
  return (
    <div className="border border-zinc-200 bg-white px-3 py-3">
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="mt-1 font-display text-2xl font-semibold text-zinc-900">{value}</p>
    </div>
  );
}

function ApprovalContextDetails({ context }) {
  if (!context) return <p className="mt-2 text-xs text-zinc-500">Approval context not available for this request.</p>;
  const policy = context.policy_snapshot || {};
  const policyChecks = policy.checks || policy;
  const checks = [
    ['Account active', policyChecks.digital_key_account_active],
    ['Selected device active', policyChecks.selected_device_active],
    ['Request not expired', policyChecks.request_not_expired],
    ['No official LoA', true],
    ['No OIDC token issued', true],
  ];
  return (
    <details className="mt-3 border-t border-zinc-100 pt-3">
      <summary className="cursor-pointer text-xs font-medium text-zinc-700">Approval context</summary>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-zinc-600">
        <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Mode</dt><dd className="mt-1">{String(context.approval_mode || '—').replaceAll('_', ' ')}</dd></div>
        <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Device-bound</dt><dd className="mt-1">{context.device_bound ? 'Yes' : 'No'}</dd></div>
        <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Device</dt><dd className="mt-1 truncate" title={context.approved_device_label || ''}>{context.approved_device_label || 'Admin override'}</dd></div>
        <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Assurance</dt><dd className="mt-1">{String(context.requested_assurance || 'internal_foundation').replaceAll('_', ' ')}</dd></div>
        <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Claims</dt><dd className="mt-1">{context.requested_claim_count || 0}</dd></div>
        <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Context</dt><dd className="mt-1">{context.approval_context_version || '—'}</dd></div>
        <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Policy</dt><dd className="mt-1">{policyStatusLabel(context.policy_status)}</dd></div>
        <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Override</dt><dd className="mt-1">{context.policy_override ? 'Yes' : 'No'}</dd></div>
      </dl>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {checks.map(([label, passed]) => <span key={label} className={`border px-2 py-1 text-[10px] ${passed ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-zinc-200 bg-zinc-50 text-zinc-600'}`}>{label}</span>)}
      </div>
    </details>
  );
}

function approvalSummaryLabel(context) {
  if (!context) return 'Admin override approval';
  if (context.approval_mode === 'consumer_acknowledged') return 'Consumer acknowledgement';
  if (context.device_bound) return 'Device-bound approval';
  return context.policy_override ? 'Admin override approval (policy override)' : 'Admin override approval';
}

export default function IdemAuthenticatorApp() {
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [devicesByAccount, setDevicesByAccount] = useState({});
  const [policiesByAccount, setPoliciesByAccount] = useState({});
  const [selectedDeviceIds, setSelectedDeviceIds] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingDenial, setPendingDenial] = useState(null);
  const [denialReason, setDenialReason] = useState('');

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'idem Authenticator Preview';
    return () => { document.title = previousTitle; };
  }, []);

  const load = useCallback(async () => {
      setLoading(true);
      setError('');
      try {
      const [requestsResponse, summaryResponse] = await Promise.all([
        api.get('/digital-key-accounts/admin/digital-key-login-requests?limit=100'),
        api.get('/digital-key-accounts/admin/digital-key-login-requests/summary'),
      ]);
      const requestList = requestsResponse.data.requests || [];
      const accountIds = [...new Set(requestList.map((request) => request.digital_key_account_id).filter(Boolean))];
      const accountResponses = await Promise.all(accountIds.map(async (accountId) => {
        const [devicesResponse, policyResponse] = await Promise.all([
          api.get(`/digital-key-accounts/admin/digital-key-accounts/${accountId}/devices`),
          api.get(`/digital-key-accounts/admin/digital-key-accounts/${accountId}/assurance-policy`),
        ]);
        return [accountId, { devices: devicesResponse.data.devices || [], policy: policyResponse.data.policy || null }];
      }));
      const nextDevicesByAccount = Object.fromEntries(accountResponses.map(([accountId, value]) => [accountId, value.devices]));
      const nextPoliciesByAccount = Object.fromEntries(accountResponses.map(([accountId, value]) => [accountId, value.policy]));
      setRequests(requestList);
      setSummary(summaryResponse.data.summary || null);
      setDevicesByAccount(nextDevicesByAccount);
      setPoliciesByAccount(nextPoliciesByAccount);
      setSelectedDeviceIds((current) => {
        const next = { ...current };
        requestList.filter((request) => request.status === 'pending').forEach((request) => {
          const activeDevices = (nextDevicesByAccount[request.digital_key_account_id] || []).filter((device) => device.status === 'active');
          if (activeDevices.length && !activeDevices.some((device) => device.id === next[request.id])) next[request.id] = activeDevices[0].id;
        });
        return next;
      });
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'Authenticator preview could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pendingRequests = useMemo(() => requests.filter((request) => request.status === 'pending'), [requests]);
  const recentRequests = useMemo(() => requests.filter((request) => request.status !== 'pending').slice(0, 12), [requests]);
  const activeDevicesForRequest = (request) => (devicesByAccount[request.digital_key_account_id] || []).filter((device) => device.status === 'active');
  const policyForRequest = (request) => policiesByAccount[request.digital_key_account_id] || null;

  const approve = async (request) => {
    const activeDevices = activeDevicesForRequest(request);
    const deviceId = selectedDeviceIds[request.id] || activeDevices[0]?.id;
    if (!deviceId) return;
    setSubmitting(true);
    setNotice(null);
    try {
      await api.post(`/digital-key-accounts/admin/digital-key-login-requests/${request.id}/approve`, { device_id: deviceId });
      setNotice({ tone: 'success', text: 'Login request approved.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The login request could not be approved.' });
    } finally {
      setSubmitting(false);
    }
  };

  const deny = async (event) => {
    event.preventDefault();
    if (!pendingDenial || !denialReason.trim()) return;
    setSubmitting(true);
    setNotice(null);
    try {
      await api.post(`/digital-key-accounts/admin/digital-key-login-requests/${pendingDenial.id}/deny`, { reason: denialReason.trim() });
      setPendingDenial(null);
      setDenialReason('');
      setNotice({ tone: 'success', text: 'Login request denied.' });
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The login request could not be denied.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-zinc-100 px-4 py-6 text-zinc-900 sm:px-6 sm:py-10" data-testid="idem-authenticator-app-page">
      <div className="mx-auto w-full max-w-xl">
        <header className="flex items-center justify-between border-b border-zinc-200 pb-5">
          <div className="flex items-center gap-2.5">
            <div className="grid h-9 w-9 place-items-center bg-black text-sm font-semibold text-white">i</div>
            <div>
              <p className="font-display text-lg font-bold tracking-tight">idem</p>
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Internal preview</span>
            </div>
          </div>
          <Link to="/internal/authenticator-preview" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-black">
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5}/> Platform view
          </Link>
        </header>

        <section className="mt-5 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5}/>
            <div>
              <p className="font-medium">Internal authenticator preview only</p>
              <p className="mt-1 text-xs leading-5">This internal preview simulates the future idem authenticator user experience. It does not create a public digital key, public login session, OIDC authorization code, token, FAS/CSAM integration, official LoA, or regulated identifier processing.</p>
            </div>
          </div>
        </section>

        {notice && <div className={`mt-5 border px-4 py-3 text-sm ${notice.tone === 'success' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-red-300 bg-red-50 text-red-700'}`}>{notice.text}</div>}

        <section className="mt-6" aria-labelledby="idem-authenticator-summary-title">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-zinc-500" strokeWidth={1.5}/>
            <h1 id="idem-authenticator-summary-title" className="font-display text-xl font-semibold">Digital key activity</h1>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <SummaryMetric label="Active keys" value={summary?.active_digital_key_accounts_count ?? '—'}/>
            <SummaryMetric label="Pending" value={summary?.pending_count ?? '—'}/>
            <SummaryMetric label="Finalized" value={summary ? (summary.approved_recent_count + summary.denied_recent_count + summary.expired_recent_count + summary.cancelled_recent_count) : '—'}/>
          </div>
        </section>

        <section className="mt-6 border border-zinc-200 bg-white p-4 sm:p-5" aria-labelledby="idem-authenticator-pending-title">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Authenticator</p>
              <h2 id="idem-authenticator-pending-title" className="mt-1 font-display text-lg font-semibold">Pending login requests</h2>
            </div>
            <span className="font-mono text-xs text-zinc-500">{pendingRequests.length}</span>
          </div>

          {loading ? <p className="mt-4 text-sm text-zinc-500">Loading requests…</p> : error ? <p className="mt-4 text-sm text-red-700">{error}</p> : pendingRequests.length ? (
            <div className="mt-4 space-y-3">
              {pendingRequests.map((request) => (
                <article key={request.id} className="border border-zinc-200 p-4">
                  {(() => {
                    const policy = policyForRequest(request);
                    const policyBlocksApproval = !policy?.can_authenticate;
                    const policyReason = policy?.blockers?.[0]?.replaceAll('_', ' ');
                    return <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-zinc-500" title={request.id}>{request.id}</p>
                      <h3 className="mt-2 text-sm font-semibold text-zinc-900">{request.identity_display_name || request.identity_ref || 'Internal idem identity'}</h3>
                      <p className="mt-1 truncate text-xs text-zinc-500">{request.identity_email || 'No email available'}</p>
                    </div>
                    <RequestStatus status={request.status}/>
                  </div>
                  <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
                    <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Assurance</dt><dd className="mt-1 text-xs text-zinc-700">{String(request.requested_assurance || 'internal_foundation').replaceAll('_', ' ')}</dd></div>
                    <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Expiry</dt><dd className="mt-1 text-xs text-zinc-700">{timeRemaining(request.expires_at)}</dd></div>
                    <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Created</dt><dd className="mt-1 text-xs text-zinc-700">{formatDate(request.created_at)}</dd></div>
                    <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Expires</dt><dd className="mt-1 text-xs text-zinc-700">{formatDate(request.expires_at)}</dd></div>
                  </dl>
                  <div className="mt-4 border-t border-zinc-100 pt-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Requested safe claims</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {request.requested_claims?.length ? request.requested_claims.map((claim) => <span key={claim} className="border border-zinc-200 bg-zinc-50 px-2 py-1 font-mono text-[10px] text-zinc-600">{claim.replaceAll('_', ' ')}</span>) : <span className="text-xs text-zinc-500">No claims requested</span>}
                    </div>
                    {request.request_context?.note && <p className="mt-3 text-xs text-zinc-500">Internal note: {request.request_context.note}</p>}
                  </div>
                  {policy && <p className={`mt-3 text-xs ${policyStyles[policy.status] || 'text-zinc-600'}`}>Policy: {policyStatusLabel(policy.status)}{policyBlocksApproval && policyReason ? ` — ${policyReason}` : ''}</p>}
                  {activeDevicesForRequest(request).length ? (
                    <label className="mt-4 block text-xs text-zinc-600">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Approve with trusted device</span>
                      {activeDevicesForRequest(request).length > 1 ? (
                        <select value={selectedDeviceIds[request.id] || ''} onChange={(event) => setSelectedDeviceIds((current) => ({ ...current, [request.id]: event.target.value }))} className="mt-1 block w-full border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none">
                          {activeDevicesForRequest(request).map((device) => <option key={device.id} value={device.id}>{device.device_label}</option>)}
                        </select>
                      ) : <p className="mt-1 border border-zinc-100 bg-zinc-50 px-3 py-2 text-sm text-zinc-700">{activeDevicesForRequest(request)[0].device_label}</p>}
                    </label>
                  ) : <p className="mt-4 border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">No active trusted device is available for this Digital Key Account. Device-bound approval cannot be simulated yet.</p>}
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => approve(request)} disabled={submitting || !activeDevicesForRequest(request).length || policyBlocksApproval} className="inline-flex items-center justify-center gap-1.5 bg-black px-3 py-2.5 text-sm text-white hover:bg-zinc-800 disabled:opacity-50"><Check className="h-4 w-4" strokeWidth={1.5}/> Approve</button>
                    <button type="button" onClick={() => { setPendingDenial(request); setDenialReason(''); }} disabled={submitting} className="inline-flex items-center justify-center gap-1.5 border border-zinc-200 px-3 py-2.5 text-sm text-zinc-700 hover:border-zinc-400 disabled:opacity-50"><X className="h-4 w-4" strokeWidth={1.5}/> Deny</button>
                  </div>
                    </>;
                  })()}
                </article>
              ))}
            </div>
          ) : <p className="mt-4 text-sm text-zinc-500">No pending login requests.</p>}
        </section>

        <section className="mt-6 border border-zinc-200 bg-white p-4 sm:p-5" aria-labelledby="idem-authenticator-recent-title">
          <div className="flex items-center gap-2">
            <Clock3 className="h-4 w-4 text-zinc-500" strokeWidth={1.5}/>
            <h2 id="idem-authenticator-recent-title" className="font-display text-lg font-semibold">Recent activity</h2>
          </div>
          {loading ? <p className="mt-4 text-sm text-zinc-500">Loading activity…</p> : recentRequests.length ? (
            <div className="mt-4 divide-y divide-zinc-100 border-y border-zinc-100">
              {recentRequests.map((request) => (
                <article key={request.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-xs text-zinc-600" title={request.id}>{request.id}</p>
                      <p className="mt-1 text-sm text-zinc-800">{request.identity_display_name || request.identity_ref || 'Internal idem identity'}</p>
                      <p className="mt-1 text-xs text-zinc-500">{request.status === 'approved' ? (request.approved_device_label ? `Approved with ${request.approved_device_label}` : approvalSummaryLabel(request.approval_context_summary)) : String(request.requested_assurance || 'internal_foundation').replaceAll('_', ' ')} · {request.requested_claims?.length || 0} claims</p>
                      {request.status === 'approved' && (
                        <ApprovalContextDetails context={request.approval_context_summary}/>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <RequestStatus status={request.status}/>
                      <p className="mt-1 text-[11px] text-zinc-500">{formatDate(request.approved_at || request.denied_at || request.expired_at || request.cancelled_at)}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : <p className="mt-4 text-sm text-zinc-500">No recent authenticator activity yet.</p>}
        </section>
      </div>

      {pendingDenial && (
        <div className="fixed inset-0 z-50 grid place-items-end bg-black/20 p-4 sm:place-items-center" role="presentation">
          <form onSubmit={deny} role="dialog" aria-modal="true" aria-labelledby="idem-authenticator-deny-title" className="w-full max-w-md border border-zinc-200 bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div><h2 id="idem-authenticator-deny-title" className="font-display text-lg font-semibold">Deny login request</h2><p className="mt-1 text-sm text-zinc-600">A reason is required for this internal decision.</p></div>
              <button type="button" onClick={() => { setPendingDenial(null); setDenialReason(''); }} disabled={submitting} className="text-zinc-500 hover:text-black" aria-label="Close denial form"><X className="h-4 w-4" strokeWidth={1.5}/></button>
            </div>
            <label className="mt-4 block font-mono text-[10px] uppercase tracking-widest text-zinc-500" htmlFor="idem-authenticator-denial-reason">Reason</label>
            <textarea id="idem-authenticator-denial-reason" value={denialReason} onChange={(event) => setDenialReason(event.target.value)} required maxLength={500} rows={3} className="mt-1 w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
            <div className="mt-4 flex justify-end gap-2"><button type="button" onClick={() => { setPendingDenial(null); setDenialReason(''); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button><button type="submit" disabled={!denialReason.trim() || submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Deny request</button></div>
          </form>
        </div>
      )}
    </main>
  );
}
